import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import QRCode from 'qrcode';

const MESSAGES = {
  cameraPermission: 'Camera access is required. Please enable it in your browser settings.',
  incompleteManual: 'Select category and price or remove item.',
  duplicateScan: 'Item already in checkout. Remove it first to scan again.',
  noScanHit: 'QR code not scanned. Try again.',
};

const MANUAL_ITEM_DEFAULT_TITLE = 'manually enter sold item';

const UI_VIEW = {
  checkout: 'checkout',
  collection: 'collection',
};

const PAYMENT_METHOD = {
  cash: 'cash',
  card: 'card',
};

const initialUiState = {
  activeManualTitleId: null,
  lockedManualLineIds: {},
  manualLineErrors: {},
  isConfirmingSale: false,
  viewMode: UI_VIEW.checkout,
  collectionSummary: null,
  isTaxExemptChecked: false,
  taxExemptBusinessName: '',
  taxExemptTaxId: '',
  paymentMethod: PAYMENT_METHOD.card,
};

function uiReducer(state, action) {
  switch (action.type) {
    case 'RESET_ON_CLOSE':
      return { ...initialUiState };
    case 'SET_ACTIVE_TITLE':
      return { ...state, activeManualTitleId: action.payload };
    case 'CLEAR_ACTIVE_TITLE_IF_MATCH':
      return {
        ...state,
        activeManualTitleId:
          state.activeManualTitleId === action.payload ? null : state.activeManualTitleId,
      };
    case 'SYNC_MANUAL_LINE_STATE':
      return {
        ...state,
        lockedManualLineIds: action.payload.lockedManualLineIds,
        manualLineErrors: action.payload.manualLineErrors,
      };
    case 'SET_MANUAL_LINE_ERROR':
      return {
        ...state,
        manualLineErrors: {
          ...state.manualLineErrors,
          [action.payload.lineId]: action.payload.message,
        },
      };
    case 'LOCK_MANUAL_LINE':
      return {
        ...state,
        manualLineErrors: {
          ...state.manualLineErrors,
          [action.payload]: '',
        },
        lockedManualLineIds: {
          ...state.lockedManualLineIds,
          [action.payload]: true,
        },
      };
    case 'SET_IS_CONFIRMING_SALE':
      return { ...state, isConfirmingSale: action.payload };
    case 'SET_COLLECTION_VIEW':
      return {
        ...state,
        viewMode: UI_VIEW.collection,
        collectionSummary: action.payload,
      };
    case 'SET_TAX_EXEMPT_CHECKED':
      return { ...state, isTaxExemptChecked: action.payload };
    case 'SET_TAX_EXEMPT_BUSINESS_NAME':
      return { ...state, taxExemptBusinessName: action.payload };
    case 'SET_TAX_EXEMPT_TAX_ID':
      return { ...state, taxExemptTaxId: action.payload };
    case 'SET_PAYMENT_METHOD':
      return { ...state, paymentMethod: action.payload };
    default:
      return state;
  }
}

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatPercentLabel = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return Number.isInteger(num)
    ? String(num)
    : num.toFixed(2).replace(/\.?0+$/, '');
};

const formatPriceValue = (value) => Number(value).toFixed(2);
const roundMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.round((number + Number.EPSILON) * 100) / 100;
};
const sanitizeQtyInput = (value) => String(value || '').replace(/\D/g, '').slice(0, 3);

const sanitizeFinalPriceInput = (value) => {
  const text = String(value ?? '');
  const cleaned = text.replace(/[^\d.]/g, '');
  const dotIndex = cleaned.indexOf('.');
  if (dotIndex === -1) return cleaned.slice(0, 7);
  const intPart = cleaned.slice(0, dotIndex).slice(0, 7);
  const fractionalRaw = cleaned.slice(dotIndex + 1).replace(/\./g, '');
  return `${intPart}.${fractionalRaw.slice(0, 2)}`;
};

const clampQty = (value, maxQty) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  const safeMaxRaw = Number.parseInt(String(maxQty ?? 999), 10);
  const safeMax = Number.isFinite(safeMaxRaw) && safeMaxRaw > 0 ? safeMaxRaw : 999;
  if (!Number.isFinite(parsed) || parsed < 1) return '1';
  return String(Math.min(parsed, safeMax));
};

const isManualLineReady = (line) => {
  const category = String(line?.category || '').trim();
  const price = Number(line?.price);
  return Boolean(category) && Number.isFinite(price) && price > 0;
};

const buildCollectionSummary = (scanItems, manualLines) => {
  const summaryItems = [];

  (scanItems || []).forEach((item) => {
    const price = Number(item?.soldPrice);
    if (!Number.isFinite(price) || price <= 0) return;
    const qty = Number.parseInt(item?.qtySold, 10);
    const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
    summaryItems.push({
      key: `scan-${item.id}`,
      qty: safeQty,
      title: String(item?.title || '').trim() || 'N/A',
      lineAmount: safeQty * price,
    });
  });

  (manualLines || []).forEach((line) => {
    const price = Number(line?.price);
    if (!Number.isFinite(price) || price <= 0) return;
    const qty = Number.parseInt(line?.qty, 10);
    const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
    const label = String(line?.title || '').trim() || MANUAL_ITEM_DEFAULT_TITLE;
    summaryItems.push({
      key: `manual-${line.id}`,
      qty: safeQty,
      title: label,
      lineAmount: safeQty * price,
    });
  });

  return {
    items: summaryItems,
    itemCount: summaryItems.reduce((sum, item) => sum + item.qty, 0),
  };
};

const getTotalItemsCount = (scanItems, manualLines) => {
  const scannedQty = (scanItems || []).reduce((sum, item) => {
    const price = Number(item?.soldPrice);
    if (!Number.isFinite(price) || price <= 0) return sum;
    const qty = Number.parseInt(item?.qtySold, 10);
    return sum + (Number.isFinite(qty) && qty > 0 ? qty : 1);
  }, 0);

  const manualQty = (manualLines || []).reduce((sum, line) => {
    const price = Number(line?.price);
    if (!Number.isFinite(price) || price <= 0) return sum;
    const qty = Number.parseInt(line?.qty, 10);
    return sum + (Number.isFinite(qty) && qty > 0 ? qty : 1);
  }, 0);

  return scannedQty + manualQty;
};

function CloseButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-[28px] leading-none text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
      aria-label="Close"
    >
      &times;
    </button>
  );
}

function QtyFinalPriceControls({
  qty,
  maxQty,
  price,
  onQtyChange,
  onPriceChange,
  compact = false,
}) {
  const normalizedMaxQty = Number.parseInt(String(maxQty ?? 999), 10);
  const isQtyFixed = Number.isFinite(normalizedMaxQty) && normalizedMaxQty <= 1;
  return (
    <div
      className={`shrink-0 self-end grid grid-cols-[3rem_6.5rem] gap-x-1 justify-items-end ${
        compact
          ? 'max-[600px]:justify-self-end max-[600px]:self-start max-[600px]:-translate-x-[20px] max-[600px]:grid-cols-[3rem_6.25rem]'
          : ''
      }`}
    >
      {!isQtyFixed ? (
        <label className="-translate-y-[5px] text-sm leading-none text-gray-700 flex flex-col items-center">
          <span className="block mb-0.5 text-center">Qty</span>
          <div className="mt-1 flex h-9 w-[4.5rem] overflow-hidden rounded-[8px] border border-[#E2E8F0] bg-white focus-within:border-[#0047AB] focus-within:ring-2 focus-within:ring-blue-500/40">
            <input
              type="number"
              min="1"
              max={maxQty}
              value={qty}
              onChange={(e) => onQtyChange(clampQty(sanitizeQtyInput(e.target.value), maxQty))}
              onFocus={(e) => e.target.select()}
              className="number-input-no-spinner h-full flex-1 border-0 bg-transparent p-0 text-center text-sm max-[600px]:text-base focus:outline-none focus:ring-0"
            />
            <div className="flex w-9 flex-col items-center justify-center border-l border-[#E2E8F0]">
              <button
                type="button"
                className="flex h-1/2 w-full items-center justify-center rounded-sm text-[10px] leading-none text-gray-600 hover:bg-gray-100"
                aria-label="Increase quantity"
                onClick={() => onQtyChange(clampQty(Number.parseInt(String(qty || '1'), 10) + 1, maxQty))}
              >
                ▲
              </button>
              <button
                type="button"
                className="flex h-1/2 w-full items-center justify-center rounded-sm border-t border-[#E2E8F0] text-[10px] leading-none text-gray-600 hover:bg-gray-100"
                aria-label="Decrease quantity"
                onClick={() => onQtyChange(clampQty(Number.parseInt(String(qty || '1'), 10) - 1, maxQty))}
              >
                ▼
              </button>
            </div>
          </div>
        </label>
      ) : (
        <div aria-hidden="true" />
      )}

      <label className="text-sm leading-none text-gray-700 flex flex-col items-center">
        <span className="block mb-0.5 text-center">Final Price</span>
        <div className="ml-[5px] flex h-9 items-center rounded-[8px] border border-[#E2E8F0] px-2 focus-within:border-[#0047AB] focus-within:ring-2 focus-within:ring-blue-500/40">
          <span className="mr-1 text-base font-semibold text-gray-600">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={price ?? ''}
            onChange={(e) => onPriceChange(sanitizeFinalPriceInput(e.target.value))}
            onFocus={(e) => e.target.select()}
            className="number-input-no-spinner h-full w-14 border-0 bg-transparent p-0 text-sm max-[600px]:text-base focus:outline-none focus:ring-0"
          />
        </div>
      </label>
    </div>
  );
}

function SoldScanModal({
  isOpen,
  scanError,
  hasMultipleSales,
  showScanner,
  scanInProgress,
  scanItems,
  manualLines,
  manualOpen,
  saleDiscountPct = 0,
  stripeCheckoutEnabled = false,
  scanBaseTotal,
  scanTaxAmount,
  scanErrorItemId,
  manualTitleOptions,
  manualTitlePriceByTitle = {},
  categoryOptions = [],
  onClose,
  onStartScan,
  onStopScan,
  onRemoveScanItem,
  onUpdateScanItem,
  onOpenManualItems,
  onAddManualLine,
  onUpdateManualLine,
  onRemoveManualLine,
  onConfirm,
}) {
  const scrollBodyRef = useRef(null);
  const manualTitleBlurTimerRef = useRef(null);
  const manualFormAnchorRef = useRef(null);
  const manualTitleInputRefs = useRef({});
  const manualCategoryInputRefs = useRef({});
  const manualPriceInputRefs = useRef({});
  const manualLineSelectedInventoryRef = useRef({});
  const addManualButtonRef = useRef(null);
  const prevUnlockedManualCountRef = useRef(0);

  const [uiState, dispatch] = useReducer(uiReducer, initialUiState);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [stripeQrDataUrl, setStripeQrDataUrl] = useState('');

  const normalizedScanError = String(scanError || '').trim();
  const isCameraPermissionError = normalizedScanError === MESSAGES.cameraPermission;
  const isMaximumQtyError = normalizedScanError.startsWith('Maximum quantity of ');
  const isIncompleteManualError = normalizedScanError === MESSAGES.incompleteManual;
  const isDuplicateScanError = normalizedScanError === MESSAGES.duplicateScan;
  const isNoScanHitError = normalizedScanError === MESSAGES.noScanHit;

  const lockedManualLines = useMemo(
    () => (manualLines || []).filter((line) => uiState.lockedManualLineIds[line.id]),
    [manualLines, uiState.lockedManualLineIds]
  );
  const unlockedManualLines = useMemo(
    () => (manualLines || []).filter((line) => !uiState.lockedManualLineIds[line.id]),
    [manualLines, uiState.lockedManualLineIds]
  );

  const hasOpenManualDraft = unlockedManualLines.length > 0;
  const hasUnsavedWork = (scanItems || []).length > 0 || (manualLines || []).length > 0;
  const totalItemsCount = getTotalItemsCount(scanItems, manualLines);
  const hasInvalidUnlockedManualLine = unlockedManualLines.some((line) => !isManualLineReady(line));
  const canSubmitSale =
    !uiState.isConfirmingSale &&
    totalItemsCount > 0 &&
    !hasInvalidUnlockedManualLine;

  const baseSubtotalAmount = Number.isFinite(Number(scanBaseTotal)) ? Number(scanBaseTotal) : 0;
  const baseTaxAmount = Number.isFinite(Number(scanTaxAmount)) ? Number(scanTaxAmount) : 0;
  const normalizedSalesTaxPct = baseSubtotalAmount > 0 ? (baseTaxAmount / baseSubtotalAmount) * 100 : 0;
  const effectiveTaxAmount = uiState.isTaxExemptChecked ? 0 : roundMoney(baseTaxAmount);
  const preCardTotalAmount = roundMoney(baseSubtotalAmount + effectiveTaxAmount);
  const cardFeeAmount = 0;
  const finalTotalAmount = roundMoney(preCardTotalAmount + cardFeeAmount);
  const finalTotalLabel = formatCurrency(finalTotalAmount);
  const isStripeCheckout = Boolean(stripeCheckoutEnabled) && uiState.paymentMethod === PAYMENT_METHOD.card;
  const stripePaymentUrl = String(uiState.collectionSummary?.stripePaymentUrl || '').trim();
  const recordedSaleNumber = Number(uiState.collectionSummary?.saleTransNumber);
  const saleRecordedLabel = Number.isInteger(recordedSaleNumber) && recordedSaleNumber > 0
    ? `Sale #${recordedSaleNumber} ${stripePaymentUrl ? 'pending payment.' : 'recorded.'}`
    : 'Sale recorded.';

  const normalizedSaleDiscountPct = Math.max(0, Math.min(100, Number(saleDiscountPct) || 0));
  const saleDiscountPctLabel = formatPercentLabel(normalizedSaleDiscountPct);
  const salesTaxPctLabel = formatPercentLabel(normalizedSalesTaxPct);
  const taxExemptBusinessNameTrimmed = String(uiState.taxExemptBusinessName || '').trim();
  const taxExemptTaxIdTrimmed = String(uiState.taxExemptTaxId || '').trim();
  const hasInvalidTaxExemptData = uiState.isTaxExemptChecked
    && (!taxExemptBusinessNameTrimmed || !taxExemptTaxIdTrimmed);
  const canSubmitSaleWithTaxRules = canSubmitSale && !hasInvalidTaxExemptData;

  useEffect(() => {
    if (!isOpen) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;
    setStripeQrDataUrl('');
    if (!stripePaymentUrl) return undefined;

    QRCode.toDataURL(stripePaymentUrl, {
      width: 260,
      margin: 1,
      errorCorrectionLevel: 'M',
    })
      .then((dataUrl) => {
        if (!cancelled) setStripeQrDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setStripeQrDataUrl('');
      });

    return () => {
      cancelled = true;
    };
  }, [stripePaymentUrl]);

  useEffect(() => {
    return () => {
      if (manualTitleBlurTimerRef.current) clearTimeout(manualTitleBlurTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) dispatch({ type: 'RESET_ON_CLOSE' });
    if (!isOpen) setShowCloseConfirm(false);
  }, [isOpen]);

  useEffect(() => {
    const present = new Set((manualLines || []).map((line) => line.id));
    const nextLocked = {};
    const nextErrors = {};

    Object.keys(uiState.lockedManualLineIds).forEach((id) => {
      if (present.has(id) && uiState.lockedManualLineIds[id]) nextLocked[id] = true;
    });
    Object.keys(uiState.manualLineErrors).forEach((id) => {
      if (present.has(id) && uiState.manualLineErrors[id]) nextErrors[id] = uiState.manualLineErrors[id];
    });

    dispatch({
      type: 'SYNC_MANUAL_LINE_STATE',
      payload: {
        lockedManualLineIds: nextLocked,
        manualLineErrors: nextErrors,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualLines]);

  useEffect(() => {
    const nextCount = unlockedManualLines.length;
    const prevCount = prevUnlockedManualCountRef.current;
    if (!manualOpen || nextCount <= prevCount) {
      prevUnlockedManualCountRef.current = nextCount;
      return;
    }
    const body = scrollBodyRef.current;
    if (!body) {
      prevUnlockedManualCountRef.current = nextCount;
      return;
    }
    requestAnimationFrame(() => {
      const anchor = manualFormAnchorRef.current;
      if (anchor && typeof anchor.scrollIntoView === 'function') {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else {
        body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
      }
    });
    prevUnlockedManualCountRef.current = nextCount;
  }, [manualOpen, unlockedManualLines.length]);

  const getManualTitleSuggestions = (value) => {
    const needle = String(value || '').trim().toLowerCase();
    if (!needle) return [];
    return (manualTitleOptions || [])
      .filter((option) => String(option?.title || option || '').toLowerCase().includes(needle))
      .filter((option) => String(option?.title || option || '').toLowerCase() !== needle)
      .slice(0, 6);
  };

  const normalizeCategoryKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const stripAndToken = (value) => String(value || '')
    .replace(/\band\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const categoryOptionByKey = useMemo(() => {
    const byKey = new Map();
    for (const raw of categoryOptions || []) {
      const label = String(raw || '').trim();
      if (!label) continue;
      const key = normalizeCategoryKey(label);
      if (!key) continue;
      if (!byKey.has(key)) byKey.set(key, label);
      const alias = stripAndToken(key);
      if (alias && !byKey.has(alias)) byKey.set(alias, label);
    }
    return byKey;
  }, [categoryOptions]);

  const resolveCategoryOption = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const normalized = normalizeCategoryKey(raw);
    if (!normalized) return raw;
    return (
      categoryOptionByKey.get(normalized)
      || categoryOptionByKey.get(stripAndToken(normalized))
      || raw
    );
  };

  const getManualMatchedPrice = (title) => {
    const key = String(title || '').trim().toLowerCase();
    if (!key) return null;
    const value = manualTitlePriceByTitle?.[key];
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num;
  };

  const findExactManualTitleOption = (title) => {
    const target = String(title || '').trim().toLowerCase();
    if (!target) return null;
    const matches = (manualTitleOptions || []).filter((option) => (
      String(option?.title || '').trim().toLowerCase() === target
    ));
    return matches.length === 1 ? matches[0] : null;
  };

const resolveDiscountedPriceFromOption = (option, discountPct) => {
  const rawPriceFromOption = Number(option?.price);
  const explicitDiscountedPrice = Number(option?.discountedPrice);
  const tagPriceFromOption = Number(option?.tagPrice);
  const optionPriceIncludesDiscount = Boolean(option?.priceIncludesDiscount);
  const discountMultiplier = Math.max(0, 1 - (discountPct / 100));
  const fallbackTagPrice = Number.isFinite(tagPriceFromOption) && tagPriceFromOption > 0
    ? Number(tagPriceFromOption.toFixed(2))
    : null;

  // Prefer explicit discounted price carried with the suggestion option.
  if (Number.isFinite(explicitDiscountedPrice) && explicitDiscountedPrice > 0) {
    return Number(explicitDiscountedPrice.toFixed(2));
  }

  // If option price is already discounted, trust it directly.
  if (Number.isFinite(rawPriceFromOption) && rawPriceFromOption > 0) {
    if (optionPriceIncludesDiscount) {
      return Number(rawPriceFromOption.toFixed(2));
    }
  }

  // Fallback: compute from tag price + current discount.
  if (Number.isFinite(tagPriceFromOption) && tagPriceFromOption > 0) {
    const discountedTagPrice = Number((tagPriceFromOption * discountMultiplier).toFixed(2));
    return discountedTagPrice > 0 ? discountedTagPrice : fallbackTagPrice;
  }

  if (Number.isFinite(rawPriceFromOption) && rawPriceFromOption > 0) {
    const discountedRawPrice = Number((rawPriceFromOption * discountMultiplier).toFixed(2));
    return discountedRawPrice > 0 ? discountedRawPrice : Number(rawPriceFromOption.toFixed(2));
  }

  return null;
};

  const applyManualSuggestion = (lineId, option) => {
    const title = String(option?.title || '').trim();
    if (title) onUpdateManualLine(lineId, 'title', title);
    if (option?.id) onUpdateManualLine(lineId, 'inventoryItemId', option.id);
    if (option?.category) {
      onUpdateManualLine(
        lineId,
        'category',
        resolveCategoryOption(String(option.category).trim())
      );
    }

    const finalPrice = resolveDiscountedPriceFromOption(option, normalizedSaleDiscountPct);
    if (finalPrice !== null && finalPrice > 0) {
      onUpdateManualLine(lineId, 'price', formatPriceValue(finalPrice));
    }
    onUpdateManualLine(lineId, 'qty', 1);
  };

  useEffect(() => {
    if (!manualOpen || !Array.isArray(manualLines) || !manualLines.length) return;

    const optionById = new Map(
      (manualTitleOptions || [])
        .filter((option) => option?.id)
        .map((option) => [String(option.id), option])
    );
    const seenLineIds = new Set();

    for (const line of manualLines) {
      const lineId = line?.id;
      const inventoryItemIdRaw = String(line?.inventoryItemId || '').trim();
      if (!lineId) continue;
      seenLineIds.add(lineId);
      const prevInventoryItemId = String(manualLineSelectedInventoryRef.current[lineId] || '').trim();

      let resolvedInventoryItemId = inventoryItemIdRaw;
      let option = null;

      if (resolvedInventoryItemId) {
        option = optionById.get(resolvedInventoryItemId) || null;
      } else {
        const titleKey = String(line?.title || '').trim().toLowerCase();
        if (titleKey) {
          const titleMatches = (manualTitleOptions || []).filter(
            (candidate) => String(candidate?.title || '').trim().toLowerCase() === titleKey
          );

          if (titleMatches.length === 1) {
            option = titleMatches[0];
          } else if (titleMatches.length > 1) {
            const currentPrice = Number(line?.price);
            const matchesByTagPrice = titleMatches.filter((candidate) => {
              const tag = Number(candidate?.tagPrice);
              return Number.isFinite(currentPrice) && Number.isFinite(tag) && Math.abs(currentPrice - tag) < 0.005;
            });
            if (matchesByTagPrice.length === 1) {
              option = matchesByTagPrice[0];
            }
          }
        }
      }

      if (!option?.id) {
        manualLineSelectedInventoryRef.current[lineId] = '';
        continue;
      }

      resolvedInventoryItemId = String(option.id);
      if (!inventoryItemIdRaw || inventoryItemIdRaw !== resolvedInventoryItemId) {
        onUpdateManualLine(lineId, 'inventoryItemId', resolvedInventoryItemId);
      }
      manualLineSelectedInventoryRef.current[lineId] = resolvedInventoryItemId;

      const tagPriceFromOption = Number(option?.tagPrice);
      const expectedDiscountedPrice = resolveDiscountedPriceFromOption(option, normalizedSaleDiscountPct);

      if (expectedDiscountedPrice === null || expectedDiscountedPrice <= 0) continue;

      const currentPrice = Number(line?.price);
      const currentIsMissing = !Number.isFinite(currentPrice) || currentPrice <= 0;
      const currentMatchesExpectedDiscounted = Number.isFinite(currentPrice)
        && Math.abs(currentPrice - expectedDiscountedPrice) < 0.005;
      const currentLooksLikeTagPrice = Number.isFinite(tagPriceFromOption)
        && Math.abs(currentPrice - tagPriceFromOption) < 0.005
        && Math.abs(currentPrice - expectedDiscountedPrice) >= 0.005;
      const selectionChanged = prevInventoryItemId !== resolvedInventoryItemId;

      if ((selectionChanged || currentIsMissing || currentLooksLikeTagPrice) && !currentMatchesExpectedDiscounted) {
        onUpdateManualLine(lineId, 'price', formatPriceValue(expectedDiscountedPrice));
      }
    }

    Object.keys(manualLineSelectedInventoryRef.current).forEach((storedLineId) => {
      if (!seenLineIds.has(storedLineId)) {
        delete manualLineSelectedInventoryRef.current[storedLineId];
      }
    });
  }, [manualLines, manualOpen, manualTitleOptions, normalizedSaleDiscountPct, onUpdateManualLine]);

  const handleCloseRequest = () => {
    if (uiState.viewMode === UI_VIEW.collection) {
      onClose();
      return;
    }
    if (!hasUnsavedWork) {
      onClose();
      return;
    }
    setShowCloseConfirm(true);
  };

  const handleBackdropClick = () => {
    if (uiState.viewMode === UI_VIEW.collection) return;
    handleCloseRequest();
  };

  const handleCancelCloseConfirm = () => {
    setShowCloseConfirm(false);
  };

  const handleConfirmCloseConfirm = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  const lockManualLine = (line) => {
    const category = String(line?.category || '').trim();
    const price = Number(line?.price);

    if (!category) {
      dispatch({
        type: 'SET_MANUAL_LINE_ERROR',
        payload: { lineId: line.id, message: 'Category is required.' },
      });
      const categoryInput = manualCategoryInputRefs.current[line.id];
      if (categoryInput?.focus) categoryInput.focus();
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      dispatch({
        type: 'SET_MANUAL_LINE_ERROR',
        payload: { lineId: line.id, message: 'Final Price is required.' },
      });
      const priceInput = manualPriceInputRefs.current[line.id];
      if (priceInput?.focus) priceInput.focus();
      return;
    }

    dispatch({ type: 'LOCK_MANUAL_LINE', payload: line.id });

    requestAnimationFrame(() => {
      const nextUnlocked = (manualLines || []).find(
        (candidate) => candidate.id !== line.id && !uiState.lockedManualLineIds[candidate.id]
      );
      if (nextUnlocked?.id) {
        const nextTitleInput = manualTitleInputRefs.current[nextUnlocked.id];
        const nextCategoryInput = manualCategoryInputRefs.current[nextUnlocked.id];
        const nextTarget = nextTitleInput || nextCategoryInput;
        if (nextTarget?.focus) nextTarget.focus();
      } else if (addManualButtonRef.current?.focus) {
        addManualButtonRef.current.focus();
      }
    });
  };

  const handleConfirmSale = async () => {
    if (!canSubmitSaleWithTaxRules) return;
    dispatch({ type: 'SET_IS_CONFIRMING_SALE', payload: true });
    try {
      const confirmResult = await onConfirm({
        paymentMethod: uiState.paymentMethod,
        useStripeCheckout: isStripeCheckout,
        isTaxExempt: uiState.isTaxExemptChecked,
        taxExemptBusinessName: taxExemptBusinessNameTrimmed || null,
        taxExemptTaxId: taxExemptTaxIdTrimmed || null,
        subtotalAmount: baseSubtotalAmount,
        taxAmount: effectiveTaxAmount,
        cardFeeAmount,
        totalAmount: finalTotalAmount,
        itemCount: totalItemsCount,
        salesTaxPct: uiState.isTaxExemptChecked ? 0 : (baseSubtotalAmount > 0 ? (baseTaxAmount / baseSubtotalAmount) * 100 : 0),
        cardFeePct: 0,
      });
      if (confirmResult) {
        const saleTransNumber = Number(confirmResult?.saleTransNumber ?? confirmResult?.sale_trans_number);
        dispatch({
          type: 'SET_COLLECTION_VIEW',
          payload: {
            ...buildCollectionSummary(scanItems, manualLines),
            saleTransNumber: Number.isInteger(saleTransNumber) && saleTransNumber > 0
              ? saleTransNumber
              : null,
            stripePaymentUrl: String(confirmResult?.stripePaymentUrl || confirmResult?.stripe_payment_url || '').trim(),
            stripeSessionId: String(confirmResult?.stripeSessionId || confirmResult?.stripe_session_id || '').trim(),
          },
        });
      }
    } finally {
      dispatch({ type: 'SET_IS_CONFIRMING_SALE', payload: false });
    }
  };

  const renderScannedItems = () => {
    if (!scanItems.length) return null;

    return (
      <div className="mt-1 space-y-2">
        {scanItems.map((item) => (
          <div
            key={item.id}
            className={`relative rounded-[8px] border px-4 pt-3 pb-3 max-[600px]:pb-1 ${
              item.id === scanErrorItemId
                ? 'border-amber-300 bg-amber-50 ring-1 ring-amber-200'
                : 'border-[#E2E8F0] bg-[#F8F9FA]'
            }`}
          >
            <button
              type="button"
              onClick={() => onRemoveScanItem(item.id)}
              className="absolute right-0 top-[-4px] inline-flex h-11 w-11 items-center justify-center text-2xl leading-none font-semibold text-gray-500 hover:text-gray-700"
              aria-label="Remove"
            >
              ×
            </button>

            <div className="grid grid-cols-[minmax(0,1fr)_10.3125rem] items-start gap-1.5 pr-[13px] max-[600px]:grid-cols-[minmax(0,1fr)_8.75rem] max-[600px]:gap-1 max-[600px]:pr-[6px]">
              <div className="min-w-0 flex-1 pr-8 max-[600px]:pr-6">
                <div className="text-base font-semibold text-gray-800 break-words max-[600px]:[overflow-wrap:anywhere] max-[600px]:pr-1">{item.title}</div>
                <div className="text-sm text-gray-500">
                  Qty Avail: {item.qtyAvailable} · Tag: {item.listPrice !== null && item.listPrice !== undefined ? formatCurrency(item.listPrice) : 'N/A'}
                </div>
              </div>

              <QtyFinalPriceControls
                compact
                qty={item.qtySold}
                maxQty={Math.min(999, item.qtyAvailable)}
                price={item.soldPrice}
                onQtyChange={(nextQty) => onUpdateScanItem(item.id, 'qtySold', nextQty)}
                onPriceChange={(nextPrice) => onUpdateScanItem(item.id, 'soldPrice', nextPrice)}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderLockedManualLines = () => {
    if (!lockedManualLines.length) return null;

    return (
      <div className="mt-1 space-y-2">
        {lockedManualLines.map((line) => (
          <div
            key={`locked-${line.id}`}
            className="relative rounded-[8px] border border-[#E2E8F0] bg-[#F8F9FA] px-4 pt-3 pb-2 max-[600px]:pb-0"
          >
            <button
              type="button"
              onClick={() => onRemoveManualLine(line.id)}
              className="absolute right-0 top-[-4px] inline-flex h-11 w-11 items-center justify-center text-2xl leading-none font-semibold text-gray-500 hover:text-gray-700"
              aria-label="Remove"
            >
              ×
            </button>

            <div className="grid grid-cols-[minmax(0,1fr)_10.3125rem] items-start gap-1.5 pr-[13px] max-[600px]:grid-cols-[minmax(0,1fr)_8.75rem] max-[600px]:gap-1 max-[600px]:pr-[6px]">
              <div className="min-w-0 flex-1 pr-8 max-[600px]:pr-6">
                <div className="text-base font-semibold text-gray-800 break-words max-[600px]:max-w-[28ch] max-[600px]:leading-tight max-[600px]:pr-1">
                  {String(line.title || '').trim() || MANUAL_ITEM_DEFAULT_TITLE}
                </div>
                <div className="text-sm text-gray-500 max-[600px]:leading-tight">
                  <span>Category: {line.category || 'N/A'}</span>
                  <span className="max-[600px]:hidden"> · Manual Item</span>
                  <span className="hidden max-[600px]:block">Manual Item</span>
                </div>
              </div>

              <QtyFinalPriceControls
                compact
                qty={line.qty}
                maxQty="999"
                price={line.price}
                onQtyChange={(nextQty) => onUpdateManualLine(line.id, 'qty', nextQty)}
                onPriceChange={(nextPrice) => onUpdateManualLine(line.id, 'price', nextPrice)}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderUnlockedManualLines = () => {
    if (!manualOpen) return null;

    return (
      <div className="mt-0 space-y-2">
        {unlockedManualLines.map((line, index) => {
          const resolvedCategoryValue = resolveCategoryOption(line.category);
          const hasResolvedCategoryInOptions = (categoryOptions || []).some(
            (option) => option === resolvedCategoryValue
          );
          return (
          <div key={line.id} className="relative rounded-[8px] border border-[#E2E8F0] bg-white px-4 pt-3 pb-3">
            <button
              type="button"
              onClick={() => onRemoveManualLine(line.id)}
              className="absolute right-1 top-1 inline-flex h-11 w-11 items-center justify-center text-2xl leading-none font-semibold text-gray-500 hover:text-gray-700"
              aria-label="Remove"
            >
              ×
            </button>

            <div className="space-y-2">
              <div className="grid grid-cols-[minmax(0,1fr)_5rem] items-end gap-3 pr-[19px] max-[600px]:grid-cols-[minmax(0,1fr)_3.75rem] max-[600px]:gap-2 max-[600px]:pr-0">
                <label className="relative min-w-0 flex-1 flex flex-col justify-start text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <span className="leading-none">Title</span>
                  </div>
                  <input
                    ref={(el) => {
                      if (el) manualTitleInputRefs.current[line.id] = el;
                    }}
                    type="text"
                    value={line.title}
                    onChange={(e) => {
                      const nextTitle = e.target.value;
                      onUpdateManualLine(line.id, 'title', nextTitle);
                      const exactMatch = findExactManualTitleOption(nextTitle);
                      if (exactMatch?.id) {
                        onUpdateManualLine(line.id, 'inventoryItemId', exactMatch.id);
                        if (exactMatch?.category) {
                          onUpdateManualLine(
                            line.id,
                            'category',
                            resolveCategoryOption(String(exactMatch.category).trim())
                          );
                        }
                        const matchedOptionPrice = resolveDiscountedPriceFromOption(
                          exactMatch,
                          normalizedSaleDiscountPct
                        );
                        if (matchedOptionPrice !== null && matchedOptionPrice > 0) {
                          onUpdateManualLine(line.id, 'price', formatPriceValue(matchedOptionPrice));
                        }
                      } else {
                        onUpdateManualLine(line.id, 'inventoryItemId', '');
                        const matchedPrice = getManualMatchedPrice(nextTitle);
                        const currentPrice = String(line.price ?? '').trim();
                        if (matchedPrice !== null && !currentPrice) {
                          onUpdateManualLine(line.id, 'price', formatPriceValue(matchedPrice));
                        }
                      }
                      if (!String(line.qty || '').trim()) {
                        onUpdateManualLine(line.id, 'qty', 1);
                      }
                      dispatch({ type: 'SET_ACTIVE_TITLE', payload: line.id });
                      dispatch({
                        type: 'SET_MANUAL_LINE_ERROR',
                        payload: { lineId: line.id, message: '' },
                      });
                    }}
                    onFocus={() => {
                      if (manualTitleBlurTimerRef.current) {
                        clearTimeout(manualTitleBlurTimerRef.current);
                      }
                      dispatch({ type: 'SET_ACTIVE_TITLE', payload: line.id });
                    }}
                    onBlur={() => {
                      manualTitleBlurTimerRef.current = setTimeout(() => {
                        dispatch({
                          type: 'CLEAR_ACTIVE_TITLE_IF_MATCH',
                          payload: line.id,
                        });
                      }, 120);
                    }}
                    placeholder="Optional (e.g., Assorted Kitchen)"
                    className="mt-1 w-full rounded-[8px] border border-[#E2E8F0] px-3 text-sm max-[600px]:text-base h-9 max-[600px]:h-11 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-[#0047AB]"
                  />

                  {uiState.activeManualTitleId === line.id && String(line.title || '').trim().length > 0 && (
                    <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-36 overflow-y-auto rounded-[8px] border border-[#E2E8F0] bg-white shadow">
                      {getManualTitleSuggestions(line.title).map((title) => (
                        <button
                          key={`${line.id}-${String(title?.id || title)}`}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            if (title && typeof title === 'object') {
                              applyManualSuggestion(line.id, title);
                            } else {
                              onUpdateManualLine(line.id, 'title', title);
                              onUpdateManualLine(line.id, 'inventoryItemId', '');
                              const matchedPrice = getManualMatchedPrice(title);
                              if (matchedPrice !== null) {
                                onUpdateManualLine(line.id, 'price', formatPriceValue(matchedPrice));
                              }
                              onUpdateManualLine(line.id, 'qty', 1);
                            }
                            dispatch({ type: 'SET_ACTIVE_TITLE', payload: null });
                            dispatch({
                              type: 'SET_MANUAL_LINE_ERROR',
                              payload: { lineId: line.id, message: '' },
                            });
                          }}
                          className="block w-full px-2 py-1.5 text-left text-sm hover:bg-gray-100"
                        >
                          {title && typeof title === 'object' ? (
                            <>
                              <div className="font-semibold text-gray-800">{title.title}</div>
                              <div className="text-sm text-gray-500">
                                {title.category || 'No category'} · {Number.isFinite(Number(title.price)) ? formatCurrency(title.price) : 'No price'} · Qty Avail {title.qtyAvailable ?? 1}
                              </div>
                            </>
                          ) : (
                            title
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </label>

                <label className="text-sm text-gray-700 flex flex-col items-center">
                  <span className="w-full text-center">Qty</span>
                  <div className="mt-1 mx-auto flex h-11 w-20 overflow-hidden rounded-[8px] border border-[#E2E8F0] bg-white focus-within:border-[#0047AB] focus-within:ring-2 focus-within:ring-blue-500/40">
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={line.qty}
                      onChange={(e) => onUpdateManualLine(line.id, 'qty', clampQty(sanitizeQtyInput(e.target.value), 999))}
                      onFocus={(e) => e.target.select()}
                      className="number-input-no-spinner h-full flex-1 border-0 bg-transparent p-0 text-center text-sm max-[600px]:text-base focus:outline-none focus:ring-0"
                    />
                    <div className="flex w-10 flex-col items-center justify-center gap-0.5 border-l border-[#E2E8F0]">
                      <button
                        type="button"
                        className="flex h-4 w-full items-center justify-center rounded-sm text-[10px] leading-none text-gray-600 hover:bg-gray-100"
                        aria-label="Increase quantity"
                        onClick={() => onUpdateManualLine(line.id, 'qty', clampQty(Number.parseInt(String(line.qty || '1'), 10) + 1, 999))}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        className="flex h-4 w-full items-center justify-center rounded-sm border-t border-[#E2E8F0] text-[10px] leading-none text-gray-600 hover:bg-gray-100"
                        aria-label="Decrease quantity"
                        onClick={() => onUpdateManualLine(line.id, 'qty', clampQty(Number.parseInt(String(line.qty || '1'), 10) - 1, 999))}
                      >
                        ▼
                      </button>
                    </div>
                  </div>
                </label>
              </div>

              <div className="mt-2 grid grid-cols-[minmax(0,1fr)_8rem] items-end gap-3 pr-[13px] max-[600px]:grid-cols-[minmax(0,1fr)_6.75rem] max-[600px]:gap-2 max-[600px]:pr-0">
                <label className="min-w-0 flex-1 text-sm text-gray-700">
                  Category
                  <select
                    ref={(el) => {
                      if (el) manualCategoryInputRefs.current[line.id] = el;
                    }}
                    value={resolvedCategoryValue || ''}
                    onChange={(e) => {
                      onUpdateManualLine(line.id, 'category', e.target.value);
                      dispatch({
                        type: 'SET_MANUAL_LINE_ERROR',
                        payload: { lineId: line.id, message: '' },
                      });
                    }}
                    className="mt-1 w-full rounded-[8px] border border-[#E2E8F0] px-3 text-sm max-[600px]:text-base h-9 max-[600px]:h-11 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-[#0047AB]"
                  >
                    <option value="">Select category</option>
                    {resolvedCategoryValue && !hasResolvedCategoryInOptions && (
                      <option value={resolvedCategoryValue}>{resolvedCategoryValue}</option>
                    )}
                    {categoryOptions.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-sm text-gray-700 flex flex-col items-end">
                  Final Price
                  <div className="mt-1 ml-[4px] max-[600px]:ml-0 flex h-9 max-[600px]:h-11 w-full items-center rounded-[8px] border border-[#E2E8F0] px-2 focus-within:border-[#0047AB] focus-within:ring-2 focus-within:ring-blue-500/40">
                    <span className="mr-1 text-base font-semibold text-gray-600">$</span>
                    <input
                      ref={(el) => {
                        if (el) manualPriceInputRefs.current[line.id] = el;
                      }}
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.price}
                      onChange={(e) => {
                        onUpdateManualLine(line.id, 'price', sanitizeFinalPriceInput(e.target.value));
                        dispatch({
                          type: 'SET_MANUAL_LINE_ERROR',
                          payload: { lineId: line.id, message: '' },
                        });
                      }}
                      onFocus={(e) => e.target.select()}
                      className="number-input-no-spinner h-full w-full border-0 bg-transparent p-0 text-sm max-[600px]:text-base focus:outline-none focus:ring-0"
                    />
                  </div>
                </label>
              </div>

              {isIncompleteManualError && index === 0 && !isManualLineReady(line) && (
                <div className="mt-1 flex justify-start">
                  <p className="rounded-md bg-amber-50 px-2 py-0.5 text-sm text-amber-700">
                    {MESSAGES.incompleteManual}
                  </p>
                </div>
              )}

              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => lockManualLine(line)}
                  disabled={!isManualLineReady(line)}
                  className={`w-full rounded-[8px] px-3 py-2 max-[600px]:py-3 text-sm max-[600px]:text-base font-semibold ${
                    isManualLineReady(line)
                      ? 'border border-[#0047AB] bg-white text-[#0047AB] hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40'
                      : 'border border-[#E2E8F0] bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Add to List
                </button>
              </div>
              {uiState.manualLineErrors[line.id] && (
                <p className="text-sm text-red-600">{uiState.manualLineErrors[line.id]}</p>
              )}
            </div>
          </div>
          );
        })}
        <div ref={manualFormAnchorRef} />
      </div>
    );
  };

  if (!isOpen) return null;

  const footerDetailTextClass = 'text-base font-normal text-gray-500';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 max-[600px]:p-0"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-lg rounded-[8px] bg-white shadow-xl max-h-[92vh] overflow-hidden flex flex-col max-[600px]:max-w-none max-[600px]:h-[100dvh] max-[600px]:max-h-[100dvh] max-[600px]:rounded-none max-[600px]:shadow-none"
        onClick={(e) => e.stopPropagation()}
      >
        {uiState.viewMode === UI_VIEW.collection ? (
          <>
            <div className="relative px-6 pt-4 max-[600px]:px-4">
              <div className="flex justify-end">
                <CloseButton onClick={handleCloseRequest} />
              </div>
            </div>

            <div className="px-6 pb-6 text-center max-[600px]:px-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-500">{saleRecordedLabel}</p>
              <p className="mt-2 text-6xl font-bold tracking-tight text-gray-900">{finalTotalLabel}</p>
              <p className="mt-2 text-base text-gray-600">
                Amount to Collect (Includes {formatCurrency(effectiveTaxAmount)} Tax)
              </p>
              <p className="mt-1 text-base font-medium text-gray-700">
                Payment: {stripePaymentUrl ? 'Stripe QR' : (uiState.paymentMethod === PAYMENT_METHOD.card ? 'Card' : 'Cash')}
              </p>
              {stripePaymentUrl && (
                <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.12em] text-blue-900">
                    Customer Payment QR
                  </p>
                  <p className="mt-1 text-sm text-blue-900">
                    Have the customer scan this code, complete payment, and show the confirmation screen.
                  </p>
                  <div className="mt-3 flex justify-center">
                    {stripeQrDataUrl ? (
                      <img
                        src={stripeQrDataUrl}
                        alt="Stripe payment QR code"
                        className="h-[220px] w-[220px] rounded-lg bg-white p-2 shadow-sm"
                      />
                    ) : (
                      <div className="flex h-[220px] w-[220px] items-center justify-center rounded-lg bg-white text-sm text-gray-500 shadow-sm">
                        Creating QR...
                      </div>
                    )}
                  </div>
                  <a
                    href={stripePaymentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-block text-sm font-semibold text-blue-700 underline"
                  >
                    Open Card payment page
                  </a>
                </div>
              )}
            </div>

            <div className="border-t border-[#E2E8F0]" />

            <div className="max-h-[200px] overflow-y-auto bg-[#F8F9FA] px-6 py-4 max-[600px]:px-4">
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-gray-500">
                {uiState.collectionSummary?.itemCount || 0} Items Sold
              </p>
              <div className="space-y-2">
                {(uiState.collectionSummary?.items || []).map((item) => (
                  <div key={item.key} className="flex items-start justify-between gap-3 text-base">
                    <div className="min-w-0 text-gray-700">
                      <span className="mr-2 font-semibold text-gray-900">{item.qty}x</span>
                      <span className="break-words">{item.title}</span>
                    </div>
                    <div className="shrink-0 font-semibold text-gray-900">{formatCurrency(item.lineAmount || 0)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#E2E8F0] bg-white px-6 py-4 max-[600px]:px-4">
              <button
                type="button"
                onClick={handleCloseRequest}
                className="w-full rounded-[8px] bg-[#0047AB] px-4 py-3 text-lg font-semibold text-white hover:bg-[#003B8F] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
              >
                Done -&gt; Next Customer
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="sticky top-0 z-20 flex items-center justify-between border-b border-[#E2E8F0] px-6 py-3 max-[600px]:px-4 bg-white">
              <div>
                <h3 className="text-2xl max-[600px]:text-[26px] font-semibold text-gray-900">Checkout</h3>
                <p className="text-sm text-gray-600">
                  Today&apos;s Discount: <span className="font-semibold">{saleDiscountPctLabel}%</span>
                </p>
              </div>
              <CloseButton onClick={handleCloseRequest} />
            </div>

            <div
              ref={scrollBodyRef}
              className={`min-h-0 flex-1 px-6 pt-2 pb-0 max-[600px]:px-4 bg-white ${
                showScanner ? 'flex flex-col overflow-hidden pb-3' : 'overflow-y-auto'
              }`}
            >
              {scanError && !isIncompleteManualError && (
                <p
                  className={`mb-2 text-sm ${
                    isCameraPermissionError || isMaximumQtyError || isDuplicateScanError || isNoScanHitError
                      ? 'text-amber-700 bg-amber-50 rounded-md px-2 py-1'
                      : 'text-red-600'
                  }`}
                >
                  {scanError}
                </p>
              )}
              {hasMultipleSales && (
                <p className="mb-2 text-sm text-red-600">
                  Scanned items belong to multiple sales. Scan items from one sale only.
                </p>
              )}

              <div className={`mb-0 mt-0 ${showScanner ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
                {showScanner && (
                  <div className="flex min-h-0 flex-1 flex-col rounded-[8px] border border-[#E2E8F0] p-3 bg-white">
                    <div className="relative min-h-0 flex-1 rounded-[8px] overflow-hidden">
                      <div id="qr-scan-reader" className="h-full w-full min-h-[260px] rounded-[8px] bg-black overflow-hidden" />
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="relative flex h-44 w-44 max-h-[70%] max-w-[70%] items-center justify-center rounded-xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.18)]">
                          <div className="h-[58%] w-[58%] rounded-lg border border-dashed border-white/70 bg-white/5 shadow-[0_0_0_1px_rgba(255,255,255,0.18)]" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex shrink-0 justify-end">
                      <button
                        type="button"
                        onClick={onStopScan}
                        className="text-sm font-semibold text-gray-600 hover:text-gray-800"
                      >
                        Stop camera
                      </button>
                    </div>
                  </div>
                )}

                {!showScanner && !scanItems.length && !lockedManualLines.length && (
                  <p className="mt-2 text-base text-gray-500">No scanned items yet.</p>
                )}

                {!showScanner && renderScannedItems()}
                {!showScanner && renderLockedManualLines()}
              </div>

              {!showScanner && (
                <div className="mb-0">
                  {renderUnlockedManualLines()}
                </div>
              )}
            </div>

            {!showScanner && (
              <div className="border-t border-[#E2E8F0] bg-white px-6 py-3 max-[600px]:px-4">
              <div className="flex items-center justify-between gap-2 max-[600px]:items-stretch">
                <button
                  type="button"
                  onClick={manualOpen ? onAddManualLine : onOpenManualItems}
                  ref={addManualButtonRef}
                  disabled={manualOpen && hasOpenManualDraft}
                  className="max-[600px]:flex-1 rounded-[8px] border border-gray-700 bg-[#F1F5F9] px-3 py-2 max-[600px]:py-3 text-sm max-[600px]:text-base font-semibold text-gray-700 hover:bg-[#E2E8F0] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                >
                  + Add Manual Item
                </button>
                <button
                  type="button"
                  onClick={onStartScan}
                  className="max-[600px]:flex-1 rounded-[8px] border border-gray-700 bg-[#F1F5F9] px-3 py-2 max-[600px]:py-3 text-sm max-[600px]:text-base font-semibold text-gray-700 hover:bg-[#E2E8F0] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                  disabled={scanInProgress || showScanner}
                >
                  {showScanner ? 'Camera Active' : (scanInProgress ? 'Starting Camera...' : '📷 Scan More Items')}
                </button>
              </div>
              {scanInProgress && !showScanner && (
                <p className="mt-2 text-sm text-gray-500">Starting camera...</p>
              )}
              {hasInvalidUnlockedManualLine && (
                <p className="mt-2 text-sm text-amber-700">
                  Complete category and final price for manual item, or remove it before confirming.
                </p>
              )}
              </div>
            )}

            {!showScanner && (
              <div className="sticky bottom-0 z-20 border-t border-[#E2E8F0] bg-white px-6 pt-2 pb-4 max-[600px]:px-4 max-[600px]:pb-3">
              {uiState.isTaxExemptChecked && (
                <div className="mb-2 grid grid-cols-2 gap-3 max-[600px]:grid-cols-1">
                  <input
                    type="text"
                    placeholder="Business Name"
                    value={uiState.taxExemptBusinessName}
                    onChange={(e) => dispatch({ type: 'SET_TAX_EXEMPT_BUSINESS_NAME', payload: e.target.value })}
                    className="h-10 max-[600px]:h-11 w-full rounded-[8px] border border-[#E2E8F0] px-3 text-sm max-[600px]:text-base text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-[#0047AB]"
                  />
                  <input
                    type="text"
                    placeholder="Tax ID #"
                    value={uiState.taxExemptTaxId}
                    onChange={(e) => dispatch({ type: 'SET_TAX_EXEMPT_TAX_ID', payload: e.target.value })}
                    className="h-10 max-[600px]:h-11 w-full rounded-[8px] border border-[#E2E8F0] px-3 text-sm max-[600px]:text-base text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-[#0047AB]"
                  />
                </div>
              )}
              <div className="mt-0 flex items-start justify-between gap-3 max-[600px]:flex-col max-[600px]:items-stretch">
                <div className="mt-0 space-y-1">
                  <label className="inline-flex items-center gap-2 text-[12px] font-medium text-gray-600">
                    <span>Exempt</span>
                    <input
                      type="checkbox"
                      checked={uiState.isTaxExemptChecked}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        dispatch({ type: 'SET_TAX_EXEMPT_CHECKED', payload: isChecked });
                        if (!isChecked) {
                          dispatch({ type: 'SET_TAX_EXEMPT_BUSINESS_NAME', payload: '' });
                          dispatch({ type: 'SET_TAX_EXEMPT_TAX_ID', payload: '' });
                        }
                      }}
                      className="h-4 w-4 rounded border-[#CBD5E1] text-[#0047AB] focus:ring-2 focus:ring-blue-500/40"
                    />
                  </label>
                  <div
                    className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 ${
                      uiState.paymentMethod === PAYMENT_METHOD.cash ? 'mt-5 max-[600px]:mt-3' : ''
                    } ${footerDetailTextClass}`}
                  >
                    <span className="truncate">Sales Tax ({salesTaxPctLabel}%)</span>
                    <span className="min-w-[5.5rem] text-right tabular-nums">{formatCurrency(effectiveTaxAmount)}</span>
                  </div>
                  {hasInvalidTaxExemptData && (
                    <div className="text-sm text-amber-700">
                      Tax exempt requires buyer business name and tax ID.
                    </div>
                  )}
                  <div className="mt-[10px] grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-3 text-[24px] leading-none font-bold text-gray-900">
                    <span>TOTAL</span>
                    <span className="min-w-[5.5rem] text-right tabular-nums">{finalTotalLabel}</span>
                  </div>
                </div>
                <div className="flex w-[228px] self-end flex-col items-end justify-end gap-2 max-[600px]:w-full max-[600px]:items-stretch">
                  <div className="mb-3 max-[600px]:mb-2 inline-flex w-full items-center rounded-[8px] border border-[#CBD5E1] bg-white p-1">
                    <button
                      type="button"
                      onClick={() => dispatch({ type: 'SET_PAYMENT_METHOD', payload: PAYMENT_METHOD.cash })}
                      className={`flex-1 rounded-[6px] px-2 py-1.5 max-[600px]:py-2 text-sm max-[600px]:text-base font-semibold ${
                        uiState.paymentMethod === PAYMENT_METHOD.cash
                          ? 'bg-[#2E8B57] text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => dispatch({ type: 'SET_PAYMENT_METHOD', payload: PAYMENT_METHOD.card })}
                      className={`flex-1 rounded-[6px] px-2 py-1.5 max-[600px]:py-2 text-sm max-[600px]:text-base font-semibold ${
                        uiState.paymentMethod === PAYMENT_METHOD.card
                          ? 'bg-[#0047AB] text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Card
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleConfirmSale}
                    disabled={!canSubmitSaleWithTaxRules}
                    className="w-full rounded-[8px] bg-[#1B9D33] px-3 py-2 max-[600px]:py-3 text-sm max-[600px]:text-base font-semibold text-white hover:bg-[#17872C] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/40"
                  >
                    {uiState.isConfirmingSale && (
                      <span className="h-3.5 w-3.5 rounded-full border-2 border-white/70 border-t-transparent animate-spin" aria-hidden="true" />
                    )}
                    {uiState.isConfirmingSale
                      ? (isStripeCheckout ? 'Creating QR...' : 'Saving...')
                      : `${isStripeCheckout ? 'Create Payment QR' : 'Complete Sale'} (${totalItemsCount} ${totalItemsCount === 1 ? 'Item' : 'Items'})`}
                  </button>
                </div>
              </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCloseConfirm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/35 px-4"
          onClick={handleCancelCloseConfirm}
        >
          <div
            className="w-full max-w-sm rounded-lg bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-base font-semibold text-gray-900">Discard this checkout?</p>
            <p className="mt-1 text-sm text-gray-600">
              This checkout has not been completed. If you leave now, the selected items in this
              checkout will be cleared and the sale will not be recorded.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelCloseConfirm}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Keep Editing
              </button>
              <button
                type="button"
                onClick={handleConfirmCloseConfirm}
                className="rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800"
              >
                Discard Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SoldScanModal;
